import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SuccessImage from '@/common/icons/successImage.svg';
import userManager from '@/api/userManager';
import { IpStatsContext } from '@/common/contexts/IpStatsContext';
import { useMutation } from 'react-query';
import { checkActive } from '@/api/query/customer';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import Loading from '@/common/components/Loading';
import useQueryMemo from '@/common/hooks/urlMemo';
import { PAYU_CHECKOUT_VERSION_NAME } from './checkout/components/payu/OneOffPayuWrapper';

const TextInfo = styled.p`
	color: gray;
	text-decoration: underline;
	font-style: italic;
	max-width: 800px;
	text-align: center;
`;
const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
	margin: 0 auto;
`;
const Title = styled.div`
	color: ${(prev) =>
		prev.isUserActive === PAYMENT_STATUS.COMPLETED
			? '#05275d'
			: prev.isUserActive === PAYMENT_STATUS.CANCELED
			? 'red'
			: 'blue'};
	text-align: center;
	font-family: Work Sans;
	font-size: 28px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	letter-spacing: -0.56px;
`;

const SubTitle = styled.div`
	color: ${(prev) => (prev.isUserActive ? '#05275d' : 'red')};

	text-align: center;
	font-family: Work Sans;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 140%; /* 22.4px */

	max-width: 300px;
`;

const ImageFailedWrapper = styled.div`
	display: flex;
	width: 84px;
	height: 84px;
	justify-content: center;
	align-items: center;
	flex-shrink: 0;
	border-radius: 103px;
	background: ${(props) =>
		props.paymentStatus === PAYMENT_STATUS.PROCESS ? 'blue' : '#f96565'};
	margin-top: 60px;

	img {
		width: 60%;
	}
`;

const ButtonMove = styled.button`
	display: flex;
	gap: 10px;
	padding: 9px 82px 10px 82px;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	color: #fff;
	text-align: center;
	font-family: 'Work Sans';
	font-size: 20px;
	font-style: normal;
	font-weight: 700;
	line-height: normal;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	background: linear-gradient(10deg, #48068a 10%, #4322c6 65%);
	margin: 5px 0;

	@media (min-width: 600px) {
		margin-top: 20px;
	}
`;

const PAYMENT_STATUS = {
	PROCESS: 'porcess',
	COMPLETED: 'COMPLETED',
	CANCELED: 'canceled',
};

const Success = () => {
	const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.PROCESS);
	const [forceSkip, setForceSkip] = useState(false);
	const langData = useLanguageData('paymentStatus');
	const queryMemo = useQueryMemo();

	const { handleFbStats, handleSendIPstats } = useContext(IpStatsContext);
	const { id } = useParams();

	const navigate = useNavigate();

	const handleGetUserInfo = useMutation({
		mutationFn: (userId) => checkActive(userId),
		onSuccess: ({ data }) => {
			let currentVersion =
				queryMemo.get('quiz_version') ||
				localStorage.getItem('quiz_variant') ||
				'LP3';

			const spPageVersion =
				queryMemo.get('sp_version') || localStorage.getItem('sp_version');
			const originVariant =
				queryMemo.get('origin_variant') ||
				localStorage.getItem('origin_variant');

			localStorage.removeItem('origin_variant');

			const { user, payments } = data;

			if (payments.length === 0) {
				return setTimeout(() => {
					const user = userManager?.getUserRegister();
					const userId = userManager?.getUser();

					if (user || id || userId) {
						handleGetUserInfo.mutate(id || user?._id || userId?.id);
					}
				}, 1000);
			}

			let checkoutPL =
				window.localStorage.getItem(PAYU_CHECKOUT_VERSION_NAME) || '';

			if (checkoutPL?.length > 0) {
				checkoutPL = `_${checkoutPL?.toUpperCase()}`;
			}

			const quizLength =
				window.localStorage.getItem('quiz_length_version') || '';

			if (payments[0]) {
				if (payments[0].status_complated_date) {
					setPaymentStatus(() => PAYMENT_STATUS.COMPLETED);

					let eventName;
					if (data.has_promo) {
						eventName = `PURCHASE_AD_${currentVersion}${quizLength}_PROMO`;
					} else if (originVariant) {
						eventName = `${originVariant}_PURCHASE`;
					} else {
						eventName =
							`PURCHASE_AD_${currentVersion}${quizLength}` +
							(spPageVersion ? `_${spPageVersion}` : '');
					}
					handleSendIPstats(eventName + checkoutPL);

					const price = (payments[0]?.price / 100).toFixed(2);
					const currency = payments[0]?.currency?.toUpperCase();

					handleFbStats('Purchase', {
						currency: currency || 'PLN',
						value: price,
					});
					window?.gtag('set', 'user_data', {
						email: localStorage.getItem('promo_email'),
					});
					window?.gtag('event', 'purchase', {
						currency: currency || 'PLN',
						value: price,
					});
					window?.gtag('event', 'conversion', {
						send_to: 'AW-16638689875/1akwCK70zMEZENOE-f09',
						value: price,
						currency: currency || 'PLN',
					});

					window?.fbq('track', 'Purchase', {
						currency: currency || 'PLN',
						value: price,
					});
					window?.uetq?.push('event', 'purchase', {
						currency: currency || 'PLN',
						revenue_value: price,
					});

					localStorage.removeItem('user_register');
					localStorage.removeItem('register_order');
				}

				if (payments[0].status_canceled_date) {
					setPaymentStatus(() => PAYMENT_STATUS.CANCELED);
					setTimeout(() => {
						navigate('/first-login');
					}, 3000);
				}
			}
		},
	});

	useEffect(() => {
		const userRegisterId = userManager?.getUserRegister();
		const user = userManager?.getUser();

		let userIdToUse = userRegisterId;

		if (!userIdToUse && !!id) {
			userIdToUse = id;
		}

		if (!userIdToUse && !!user) {
			userIdToUse = user.id;
		}

		if (userIdToUse) {
			handleGetUserInfo.mutate(userIdToUse);
		} else {
			setTimeout(() => {
				navigate('/first-login');
			}, 3000);
		}
		setTimeout(() => {
			setForceSkip(() => true);
		}, 30000);
	}, []);

	return (
		<>
			{handleGetUserInfo.isLoading && <Loading />}
			<Wrapper>
				{paymentStatus === PAYMENT_STATUS.COMPLETED ? (
					<img
						alt=''
						src={SuccessImage}
						style={{ width: '80px', height: '80px', marginTop: '60px' }}
					/>
				) : (
					<ImageFailedWrapper paymentStatus={paymentStatus} />
				)}
				<Title isUserActive={paymentStatus}>
					{paymentStatus === PAYMENT_STATUS.COMPLETED
						? langData.success
						: PAYMENT_STATUS.CANCELED === paymentStatus
						? langData.failure
						: langData.veryfication}
				</Title>
				{[PAYMENT_STATUS.CANCELED, PAYMENT_STATUS.COMPLETED].includes(
					paymentStatus,
				) && (
					<SubTitle isUserActive={PAYMENT_STATUS.COMPLETED === paymentStatus}>
						{langData.subtitle}
					</SubTitle>
				)}
				{([PAYMENT_STATUS.COMPLETED].includes(paymentStatus) || forceSkip) && (
					<>
						<ButtonMove onClick={() => navigate('/first-login')}>
							{langData.login}
						</ButtonMove>
						<TextInfo>{langData.textInfo}</TextInfo>
					</>
				)}
			</Wrapper>
		</>
	);
};

export default Success;
